// Unsaved changes, one mechanism for the whole admin (DESIGN.md «Несохранённые изменения»): a screen with a working copy registers
// whether it holds unsaved work and how to save it; every way out — the rail, the column, the crumbs, «назад», a switch inside the
// screen, closing the tab — asks one question of the core: «Сохранить / Не сохранять / Остаться».

import { useEffect, useRef, useSyncExternalStore } from 'react';

export interface UnsavedGuard {
  /** Says whether the working copy differs from what is saved, at the moment of asking. */
  dirty(): boolean;
  /** Saves the working copy; false — the save failed, the screen has said why, and the person stays. */
  save(): Promise<boolean> | boolean;
  /** What would be lost, for the question: «страница сайта», «сценарий». */
  what: string;
  /** The modal it lives in (a layer of the stack): closing that layer asks only about its own work, not about the layers under it. */
  scope?: string;
}

export type LeaveChoice = 'save' | 'discard' | 'stay';
export interface LeaveQuestion { what: string[]; saving: boolean }

const guards = new Set<UnsavedGuard>();
let question: LeaveQuestion | null = null;
let answerWith: ((c: LeaveChoice) => void) | null = null;
const listeners = new Set<() => void>();
const notify = () => { for (const l of listeners) l(); };

/** Adds a guard; the returned function removes it (a screen does it when it closes). */
export function registerUnsaved(g: UnsavedGuard): () => void {
  guards.add(g);
  return () => { guards.delete(g); };
}

const dirtyGuards = () => [...guards].filter((g) => g.dirty());
export const hasUnsaved = () => dirtyGuards().length > 0;

/**
 * Runs `go` when nothing is unsaved — at once, so an ordinary click stays synchronous; otherwise asks first and runs it after
 * «Не сохранять» or a successful «Сохранить». «Остаться», a failed save, or a question already open leave things as they are.
 */
export function leaveThen(go: () => void, scopes?: readonly string[]): void {
  const dirty = dirtyGuards().filter((g) => !scopes || (g.scope !== undefined && scopes.includes(g.scope)));
  if (!dirty.length) { go(); return; }
  if (question) return;
  void ask(dirty).then((ok) => { if (ok) go(); });
}

async function ask(dirty: UnsavedGuard[]): Promise<boolean> {
  question = { what: dirty.map((g) => g.what), saving: false };
  notify();
  try {
    const choice = await new Promise<LeaveChoice>((resolve) => { answerWith = resolve; });
    if (choice === 'stay') return false;
    if (choice === 'discard') return true;
    question = { ...question!, saving: true };
    notify();
    // A failed save keeps the person on the screen: the screen shows the reason, the working copy stays.
    for (const g of dirty) if (g.dirty() && !(await g.save())) return false;
    return true;
  } finally {
    question = null; answerWith = null;
    notify();
  }
}

/** The dialog's answer. */
export function answerLeave(c: LeaveChoice): void { answerWith?.(c); }

/** The open question, for the dialog of the frame. */
export function useLeaveQuestion(): LeaveQuestion | null {
  return useSyncExternalStore((cb) => { listeners.add(cb); return () => listeners.delete(cb); }, () => question, () => null);
}

/** Closing or reloading the tab: the browser asks with its own words — a page cannot ask its own question there. */
export function onBeforeUnload(e: { preventDefault(): void; returnValue?: unknown }): void {
  if (!hasUnsaved()) return;
  e.preventDefault();
  e.returnValue = '';
}
if (typeof window !== 'undefined') window.addEventListener('beforeunload', onBeforeUnload);

/**
 * A screen's guard for as long as it is open. `dirty` and `save` are read at the moment of asking, so the latest working copy is
 * saved, not the one of the render that registered the guard.
 */
export function useUnsaved(dirty: boolean, save: () => Promise<boolean> | boolean, what: string, scope?: string): void {
  const cur = useRef({ dirty, save, what });
  cur.current = { dirty, save, what };
  useEffect(() => registerUnsaved({ dirty: () => cur.current.dirty, save: () => cur.current.save(), get what() { return cur.current.what; }, scope }), [scope]);
}
